import { useCallback, useState } from 'react'
import type { CommunicationMethod } from 'types/communications'
import type {
  SendListRecord,
  SendRecord,
  TransactionalPreviewPayload,
  TransactionalPreviewResult,
  TransactionalSendPayload,
} from 'types/sends'
import type { MutationResult, QueryResult } from 'utils/api'
import { qs } from 'utils/api'

import { useApiQuery } from '../useApiQuery'
import { useFetchApi } from '../useFetchApi'

export interface SendsParams {
  skip?: number
  take?: number
  scheduledCommunicationId?: string
  scheduleId?: string
  method?: CommunicationMethod
  contactId?: string
}

export function useSends(params?: SendsParams): QueryResult<SendListRecord[]> {
  // Always summary mode: every consumer is a LIST view, and full rows carry
  // the complete recipients array (one PII entry per person — unbounded for
  // broadcasts) plus provider messages and rendered bodies. Detail views use
  // useSend(id) for the full record.
  return useApiQuery<SendListRecord[]>(`/api/sends${qs({ ...params, summary: true })}`)
}

export function useSend(id: string | undefined): QueryResult<SendRecord> {
  return useApiQuery<SendRecord>(id ? `/api/sends/${id}` : null)
}

export function usePreviewTransactional(): MutationResult<TransactionalPreviewResult, [TransactionalPreviewPayload]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (payload: TransactionalPreviewPayload): Promise<TransactionalPreviewResult> => {
      setLoading(true)
      try {
        return await fetchApi<TransactionalPreviewResult>('/api/sends/preview', {
          method: 'POST',
          body: JSON.stringify(payload),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useSendTransactional(): MutationResult<SendRecord, [TransactionalSendPayload]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (payload: TransactionalSendPayload): Promise<SendRecord> => {
      setLoading(true)
      try {
        return await fetchApi<SendRecord>('/api/sends', {
          method: 'POST',
          body: JSON.stringify(payload),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}
